import React, { useState, useEffect, useRef, useCallback } from "react";
import { Building2, Mail, Phone, Globe, Clock, Save, Check, Calendar, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { useSiteContext } from "../../../context/SiteContext";
import userService from "../../../services/userServices";
import calendarService from "../../../services/calendarServices";

const timezones = [
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "America/New_York (GMT-05:00)" },
  { value: "America/Chicago", label: "America/Chicago (GMT-06:00)" },
  { value: "America/Denver", label: "America/Denver (GMT-07:00)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (GMT-08:00)" },
  { value: "Europe/London", label: "Europe/London (GMT+00:00)" },
  { value: "Europe/Paris", label: "Europe/Paris (GMT+01:00)" },
  { value: "Europe/Brussels", label: "Europe/Brussels (GMT+01:00)" },
  { value: "Europe/Amsterdam", label: "Europe/Amsterdam (GMT+01:00)" },
  { value: "Europe/Belgrade", label: "Europe/Belgrade (GMT+01:00)" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo (GMT+09:00)" },
  { value: "Asia/Shanghai", label: "Asia/Shanghai (GMT+08:00)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GMT+04:00)" },
  { value: "Australia/Sydney", label: "Australia/Sydney (GMT+10:00)" },
  { value: "Australia/Brisbane", label: "Australia/Brisbane (GMT+10:00)" },
  { value: "Australia/Hobart", label: "Australia/Hobart (GMT+10:00)" },
  { value: "Pacific/Auckland", label: "Pacific/Auckland (GMT+12:00)" },
];

const languages = [
  "English (US)",
  "English (UK)",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Chinese",
  "Japanese",
];

const CalendarProviderRow = ({
  providerKey,
  name,
  logo,
  status,
  loading,
  onConnect,
  onDisconnect,
  calendars,
  calendarsLoading,
  onSelectCalendar,
}) => {
  const isConnected = !!status?.connected;
  const isComingSoon = !!status?.comingSoon;
  const hasError = !!status?.lastError;
  const supportsCalendarPicker = providerKey === "google";
  const selectedCalendarId = status?.calendarId || "primary";

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center overflow-hidden">
            {logo}
          </div>
          <div>
            <div className="text-white font-medium">{name}</div>
            {isConnected && status?.accountEmail && (
              <div className="text-xs text-gray-400">
                Connected as {status.accountEmail}
              </div>
            )}
            {isConnected && !status?.accountEmail && (
              <div className="text-xs text-gray-400">Connected</div>
            )}
            {!isConnected && isComingSoon && (
              <div className="text-xs text-gray-400">Coming soon</div>
            )}
            {!isConnected && !isComingSoon && (
              <div className="text-xs text-gray-400">Not connected</div>
            )}
            {hasError && (
              <div className="text-xs text-red-400 flex items-center gap-1 mt-1">
                <AlertCircle className="w-3 h-3" />
                {status.lastError}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isConnected ? (
            <>
              <span className="px-2 py-1 text-xs rounded-full bg-[#00c853]/20 text-[#00c853] border border-[#00c853]/30">
                Active
              </span>
              <button
                type="button"
                onClick={onDisconnect}
                disabled={loading}
                className="px-4 py-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Working…
                  </span>
                ) : (
                  "Disconnect"
                )}
              </button>
            </>
          ) : isComingSoon ? (
            <button
              type="button"
              disabled
              className="px-4 py-2 text-sm bg-white/5 border border-white/10 text-gray-400 rounded-lg cursor-not-allowed"
            >
              Coming soon
            </button>
          ) : (
            <button
              type="button"
              onClick={onConnect}
              disabled={loading}
              className="px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 font-medium"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Connecting…
                </span>
              ) : (
                "Connect"
              )}
            </button>
          )}
        </div>
      </div>

      {isConnected && supportsCalendarPicker && (
        <div className="px-4 pb-4 pt-0 border-t border-white/10 mt-0">
          <label className="block text-xs font-medium text-gray-400 mt-3 mb-2">
            Push events to this calendar
          </label>
          {calendarsLoading ? (
            <div className="text-xs text-gray-400 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading your calendars…
            </div>
          ) : calendars && calendars.length > 0 ? (
            <select
              value={selectedCalendarId}
              onChange={(e) => onSelectCalendar(e.target.value)}
              className="w-full md:w-96 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 0.75rem center",
                paddingRight: "2rem",
              }}
            >
              {calendars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.summary}
                  {c.primary ? " (primary)" : ""}
                  {c.accessRole === "writer" ? " — shared" : ""}
                </option>
              ))}
            </select>
          ) : hasError ? (
            <div className="text-xs text-red-400 flex items-start gap-1">
              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>
                Couldn't load calendars: {status.lastError}. Try
                disconnecting and reconnecting.
              </span>
            </div>
          ) : (
            <div className="text-xs text-gray-400">
              No writable calendars returned. If you just connected, the
              Google Calendar API or scope may not be enabled — disconnect
              and reconnect after fixing the Google Cloud Console setup.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Account = () => {
  const { user, setUser } = useSiteContext();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    companyName: "",
    ownerName: "",
    email: "",
    phone: "",
    timezone: "UTC",
    language: "English (US)",
  });

  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  const [calendarStatus, setCalendarStatus] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState({ google: false, microsoft: false });
  const [calendarLists, setCalendarLists] = useState({ google: [], microsoft: [] });
  const [calendarListLoading, setCalendarListLoading] = useState({ google: false, microsoft: false });

  const refreshCalendarStatus = useCallback(async () => {
    try {
      const res = await calendarService.getStatus();
      if (res?.success) setCalendarStatus(res.providers);
    } catch (err) {
      console.error("Failed to load calendar status", err);
    }
  }, []);

  const fetchCalendarsFor = useCallback(async (provider) => {
    setCalendarListLoading((prev) => ({ ...prev, [provider]: true }));
    try {
      const res = await calendarService.listCalendars(provider);
      if (res?.success) {
        setCalendarLists((prev) => ({ ...prev, [provider]: res.calendars || [] }));
      } else {
        setCalendarLists((prev) => ({ ...prev, [provider]: [] }));
      }
    } catch (err) {
      console.error(`Failed to load ${provider} calendars`, err);
      setCalendarLists((prev) => ({ ...prev, [provider]: [] }));
    } finally {
      setCalendarListLoading((prev) => ({ ...prev, [provider]: false }));
    }
  }, []);

  const handleSelectCalendar = async (provider, calendarId) => {
    try {
      const res = await calendarService.setCalendarId(provider, calendarId);
      if (res?.success) {
        toast.success("Calendar updated");
        // Optimistically reflect the change locally
        setCalendarStatus((prev) =>
          prev
            ? {
                ...prev,
                [provider]: { ...(prev[provider] || {}), calendarId },
              }
            : prev
        );
      } else {
        toast.error(res?.message || "Could not update calendar");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not update calendar");
    }
  };

  useEffect(() => {
    refreshCalendarStatus();
  }, [refreshCalendarStatus]);

  // Whenever a provider becomes connected, fetch its calendar list so the
  // user can pick a non-primary calendar.
  useEffect(() => {
    if (calendarStatus?.google?.connected) {
      fetchCalendarsFor("google");
    }
  }, [calendarStatus?.google?.connected, fetchCalendarsFor]);

  // Listen for the popup's postMessage when OAuth completes.
  useEffect(() => {
    const handler = (event) => {
      const data = event?.data;
      if (!data || data.source !== "rlink-calendar-oauth") return;
      if (data.status === "connected") {
        toast.success(data.message || "Calendar connected");
        refreshCalendarStatus();
        // Also force a calendar-list refetch (covers the case where the
        // status had `connected:true` already from a stale fetch and the
        // [connected] effect doesn't re-fire).
        fetchCalendarsFor("google");
      } else if (data.status === "error") {
        toast.error(data.message || "Calendar connection failed");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [refreshCalendarStatus, fetchCalendarsFor]);

  const handleConnectCalendar = async (provider) => {
    setCalendarLoading((prev) => ({ ...prev, [provider]: true }));
    try {
      const res = await calendarService.getConnectUrl(provider);
      if (!res?.success || !res.authUrl) {
        toast.error(res?.message || "Could not start calendar connection");
        return;
      }
      const w = 540;
      const h = 680;
      const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
      const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
      const popup = window.open(
        res.authUrl,
        "rlink-calendar-oauth",
        `width=${w},height=${h},left=${left},top=${top}`
      );
      if (!popup) {
        toast.error("Popup blocked. Please allow popups and try again.");
        return;
      }

      // Cross-origin popups can lose `postMessage` (popup blockers, COOP
      // headers, etc.). As a fallback, poll the popup's closed state and
      // refresh status once it's gone. Cap at 5 minutes.
      const pollStartedAt = Date.now();
      const interval = setInterval(async () => {
        const closed = popup.closed;
        const timedOut = Date.now() - pollStartedAt > 5 * 60 * 1000;
        if (closed || timedOut) {
          clearInterval(interval);
          // Allow the backend a moment to commit the connection.
          setTimeout(() => {
            refreshCalendarStatus();
            if (provider === "google") fetchCalendarsFor("google");
          }, 300);
        }
      }, 700);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to connect calendar");
    } finally {
      setCalendarLoading((prev) => ({ ...prev, [provider]: false }));
    }
  };

  const handleDisconnectCalendar = async (provider) => {
    if (!window.confirm("Disconnect this calendar? Future webinars will no longer be added automatically.")) {
      return;
    }
    setCalendarLoading((prev) => ({ ...prev, [provider]: true }));
    try {
      const res = await calendarService.disconnect(provider);
      if (res?.success) {
        toast.success("Calendar disconnected");
        refreshCalendarStatus();
      } else {
        toast.error(res?.message || "Failed to disconnect");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to disconnect");
    } finally {
      setCalendarLoading((prev) => ({ ...prev, [provider]: false }));
    }
  };

  // Fetch fresh profile data on mount to get latest companyName, timezone, etc.
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await userService.getUserProfile();
        if (response.success && response.user) {
          setUser(response.user);
          localStorage.setItem("user", JSON.stringify(response.user));
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) {
      setFormData({
        companyName: user.companyName || "",
        ownerName: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        timezone: user.timezone || "UTC",
        language: user.language || "English (US)",
      });

      if (user.logo) {
        setProfileImagePreview(user.logo);
      }
    }
  }, [user]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleChange(name, value);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should not exceed 5MB");
        return;
      }

      setProfileImage(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveChanges = async () => {
    if (!formData.email.trim()) {
      toast.error("Contact email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        companyName: formData.companyName,
        name: formData.ownerName,
        email: formData.email,
        phone: formData.phone,
        timezone: formData.timezone,
        language: formData.language,
      };

      const response = await userService.updateUserProfile(updateData);

      if (response.user) {
        setUser(response.user);
        localStorage.setItem("user", JSON.stringify(response.user));
        toast.success("Account settings saved successfully");
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (error) {
      console.error("Error updating account:", error);
      toast.error(
        error.response?.data?.message || "Failed to update account settings"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 p-3 md:p-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Account Profile</h2>
        <p className="text-gray-400">
          Manage your organization's basic information
        </p>
      </div>

      {/* Organization Details */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#6a1fbf]" />
          Organization Details
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Company / Organization Name
            </label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              placeholder="Enter company name"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Owner / Admin Name
            </label>
            <input
              type="text"
              name="ownerName"
              value={formData.ownerName}
              onChange={handleInputChange}
              placeholder="Enter name"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Mail className="w-4 h-4 inline mr-1" />
              Contact Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              disabled
              placeholder="email@company.com"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-gray-400 placeholder-gray-500 cursor-not-allowed opacity-60 focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Phone className="w-4 h-4 inline mr-1" />
              Phone (Optional)
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+1 (555) 000-0000"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Regional Settings */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Globe className="w-5 h-5 text-[#6a1fbf]" />
          Regional Settings
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Timezone
            </label>
            <select
              name="timezone"
              value={formData.timezone}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 1rem center",
              }}
            >
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Default Locale / Language
            </label>
            <select
              name="language"
              value={formData.language}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 1rem center",
              }}
            >
              {languages.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Calendar Integration */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#6a1fbf]" />
            Calendar Integration
          </h3>
          <p className="text-gray-400 text-sm mt-1">
            Connect a calendar so every webinar you create is automatically added with full event details and the join link.
          </p>
        </div>

        {/* Google Calendar */}
        <CalendarProviderRow
          providerKey="google"
          name="Google Calendar"
          logo={
            <svg viewBox="0 0 200 200" className="w-8 h-8" aria-hidden="true">
              <path fill="#fff" d="M44 44h112v112H44z"/>
              <path fill="#1A73E8" d="M156 44H44v112h112V44zm-19 90h-12v-12h-12v12h-12v-12H89v-12h12V98H89V86h12V74h12v12h12V74h12v12h-12v12h12v12h-12v12h12v12z" opacity="0"/>
              <path fill="#4285F4" d="M44 44h112v40H44z"/>
              <path fill="#34A853" d="M156 156V84h-40v40h-32v32z"/>
              <path fill="#FBBC04" d="M84 124v32h32v-32z"/>
              <path fill="#EA4335" d="M44 84v72h40v-32h32V84z"/>
              <text x="100" y="125" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="56" fill="#1A73E8">31</text>
            </svg>
          }
          status={calendarStatus?.google}
          loading={calendarLoading.google}
          onConnect={() => handleConnectCalendar("google")}
          onDisconnect={() => handleDisconnectCalendar("google")}
          calendars={calendarLists.google}
          calendarsLoading={calendarListLoading.google}
          onSelectCalendar={(id) => handleSelectCalendar("google", id)}
        />

        {/* Microsoft / Outlook */}
        <CalendarProviderRow
          providerKey="microsoft"
          name="Outlook / Microsoft 365"
          logo={
            <svg viewBox="0 0 48 48" className="w-8 h-8" aria-hidden="true">
              <path fill="#0078D4" d="M21 7H7v34h14V7z"/>
              <path fill="#0078D4" d="M21 24l20-12v24z" opacity=".4"/>
              <path fill="#28A8EA" d="M14 16a8 8 0 100 16 8 8 0 000-16zm0 13a5 5 0 110-10 5 5 0 010 10z"/>
            </svg>
          }
          status={calendarStatus?.microsoft}
          loading={calendarLoading.microsoft}
          onConnect={() => handleConnectCalendar("microsoft")}
          onDisconnect={() => handleDisconnectCalendar("microsoft")}
          calendars={calendarLists.microsoft}
          calendarsLoading={calendarListLoading.microsoft}
          onSelectCalendar={(id) => handleSelectCalendar("microsoft", id)}
        />
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveChanges}
          disabled={loading}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed font-bold"
        >
          {loading ? (
            <>Saving...</>
          ) : saved ? (
            <>
              <Check className="w-4 h-4" /> Saved
            </>
          ) : (
            <>
              <Save className="w-4 h-4" /> Save Changes
            </>
          )}
        </button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        className="hidden"
      />
    </div>
  );
};

export default Account;
